import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import s from './formular.module.css';

/** Suchfeld 48 px (GL-023 §9.9). Label ist fuer Screenreader da, sichtbar ist der Platzhalter. */
export const Suchfeld = forwardRef<HTMLInputElement, { label: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>>(
  function Suchfeld({ label, ...rest }, ref) {
    const id = useId();
    return (
      <div role="search" className={s.suche}>
        <label htmlFor={id} className="nb-sr-only">
          {label}
        </label>
        <span className={s.sucheIcon}>
          <Icon name="suche" groesse={20} strich={1.9} />
        </span>
        <input ref={ref} id={id} type="search" className={s.sucheFeld} {...rest} />
      </div>
    );
  },
);

/** Eingabefeld mit Label, Hilfe und Fehlertext (GL-023 §9.10). Fehler immer mit Icon und Klartext. */
export function Eingabefeld({
  label,
  hilfe,
  fehler,
  badge,
  ...rest
}: {
  label: string;
  hilfe?: ReactNode;
  fehler?: string | null;
  /** Herkunfts-Badge rechts neben dem Label ("berechnet", "geschaetzt") */
  badge?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const hilfeId = `${id}-hilfe`;
  const fehlerId = `${id}-fehler`;
  const beschrieben = [fehler ? fehlerId : null, hilfe ? hilfeId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className={s.feld}>
      <label htmlFor={id} className={s.label} style={badge ? { display: 'flex', justifyContent: 'space-between' } : undefined}>
        {label}
        {badge}
      </label>
      <input id={id} className={s.eingabe} aria-invalid={fehler ? true : undefined} aria-describedby={beschrieben} {...rest} />
      {fehler && (
        <span id={fehlerId} className={s.fehlerText}>
          <Icon name="fehlerKreis" groesse={18} strich={2} />
          {fehler}
        </span>
      )}
      {hilfe && (
        <span id={hilfeId} className={s.hilfe}>
          {hilfe}
        </span>
      )}
    </div>
  );
}

export interface SegmentOption<T extends string> {
  wert: T;
  label: string;
  icon?: IconName;
}

/** Segmentschalter 44 px (GL-023 §9.10), z. B. Liste/Kalender oder Nach Kategorie/Nach Rezept. */
export function Segment<T extends string>({
  label,
  optionen,
  wert,
  onWechsel,
}: {
  label: string;
  optionen: readonly SegmentOption<T>[];
  wert: T;
  onWechsel: (neu: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className={s.segment}>
      {optionen.map((o) => {
        const aktiv = o.wert === wert;
        return (
          <button key={o.wert} type="button" className={s.segmentKnopf} aria-pressed={aktiv} onClick={() => onWechsel(o.wert)}>
            {o.icon && <Icon name={o.icon} groesse={18} strich={aktiv ? 1.9 : 1.8} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Sortierknopf "Neueste zuerst ⌄" (Canvas R3/R4). Ein natives select liegt unsichtbar darueber,
 * damit Handy und Screenreader die gewohnte Auswahl bekommen.
 */
export function Sortierung<T extends string>({
  label,
  wert,
  optionen,
  onWechsel,
}: {
  label: string;
  wert: T;
  optionen: ReadonlyArray<{ wert: T; label: string }>;
  onWechsel: (neu: T) => void;
}) {
  const aktuell = optionen.find((o) => o.wert === wert)?.label ?? '';
  return (
    <span className={s.sortierung}>
      <span aria-hidden="true" className={s.sortierungText}>
        {aktuell}
        <Icon name="chevronUnten" groesse={18} strich={2} />
      </span>
      <select
        aria-label={label}
        className={s.sortierungSelect}
        value={wert}
        onChange={(e) => {
          const neu = optionen.find((o) => o.wert === e.target.value);
          if (neu) onWechsel(neu.wert);
        }}
      >
        {optionen.map((o) => (
          <option key={o.wert} value={o.wert}>
            {o.label}
          </option>
        ))}
      </select>
    </span>
  );
}
